import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  ModalProps,
  ModalContentProps,
  ModalBodyProps,
  TooltipProps,
  Tooltip
} from "@chakra-ui/react";
import { RefObject, useEffect, useRef, useState } from "react";
import { dataSyncEmitter } from "@/modules/events/emitter";
import { useTooltips } from "@/lib/TooltipManager";

type Props = {
  title?: string | React.ReactNode;
  isOpen: boolean;
  onClose: Cb;
  props?: Omit<ModalProps, "onClose" | "isOpen" | "children">;
  footerContent?: React.ReactNode;
  closeButton?: boolean;
  children?: React.ReactNode;
  modalContentProps?: ModalContentProps;
  modalBodyProps?: ModalBodyProps;
  disableNavigationEvents?: boolean;
  tooltip?: Omit<TooltipProps, "children">;
};

const modalEscEvent = new Event("modal-esc");
const handlePreventedClose = (source: ModalCloseSource) => dataSyncEmitter.emit("modal-prevented-close", source);

export default function Modal({
  title,
  isOpen,
  onClose,
  footerContent,
  closeButton,
  props,
  children,
  modalContentProps,
  modalBodyProps,
  tooltip,
  // HOTFIXED situation where two modals are opened and one of them has NavigationBar
  disableNavigationEvents
}: Props) {
  const { closeOnEsc = true, closeOnOverlayClick = true } = props || {};
  const [navigationEvents, setNavigationEvents] = useState({ closeOnEsc, closeOnOverlayClick });

  const containerRef = useRef<HTMLDivElement>(null);
  const [bodyStyles, setBodyStyles] = useState<ModalBodyProps>({ overflow: "auto" });
  const tooltipController = useTooltips();

  useEffect(() => {
    const onNavigationEventsUpdate = (u: boolean) => setNavigationEvents({ closeOnEsc: !u, closeOnOverlayClick: !u });
    const onNavigationBarEvent = (u: boolean) => setNavigationEvents((p) => ({ ...p, closeOnEsc: !u }));

    const unsub = [
      dataSyncEmitter.on("flow-transition-start", () => setBodyStyles({ overflow: "hidden" })),
      dataSyncEmitter.on("flow-transition-end", () => setBodyStyles({ overflow: "auto" })),
      ...(disableNavigationEvents
        ? []
        : [
            dataSyncEmitter.on("set-navigation-events-by-navigation-bar", onNavigationBarEvent),
            dataSyncEmitter.on("set-navigation-events", onNavigationEventsUpdate)
          ])
    ];
    return () => unsub.forEach((cb) => cb());
  }, [disableNavigationEvents]);

  return (
    <ChakraModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      isCentered
      scrollBehavior="inside"
      onOverlayClick={() => {
        if (!navigationEvents.closeOnOverlayClick) handlePreventedClose("overlay");
      }}
      onEsc={() => {
        if (!navigationEvents.closeOnEsc) handlePreventedClose("esc");
        if (!disableNavigationEvents) document.dispatchEvent(modalEscEvent);
      }}
      initialFocusRef={containerRef as RefObject<HTMLDivElement>}
      {...props}
      {...navigationEvents}
    >
      <ModalOverlay />
      <ModalContent minH={484} {...modalContentProps} ref={containerRef}>
        {closeButton && (
          <Tooltip
            {...(tooltip ? { gutter: 4, ...tooltip } : { isDisabled: true })}
            isOpen={tooltipController.isOpen}
            onClose={tooltipController.close}
          >
            <ModalCloseButton
              onClick={() => {
                tooltipController.close();
              }}
              onPointerEnter={tooltipController.open}
              top="16"
              right="16"
              zIndex={10}
            />
          </Tooltip>
        )}
        {title && <ModalHeader>{title}</ModalHeader>}
        <ModalBody {...bodyStyles} {...modalBodyProps}>
          {children}
        </ModalBody>
        {footerContent && <ModalFooter>{footerContent}</ModalFooter>}
      </ModalContent>
    </ChakraModal>
  );
}
